import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, RotateCcw } from "lucide-react";
import { getSessionToken } from "@/utils/auth";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const fmt = (n) => (n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

function MaterialLine({ line, value, onChange }) {
  const changed = value !== "" && Number(value) !== line.default_unit_cost;
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={`material-line-${line.index}`}>
      <div className="flex-1 min-w-0">
        <div className="truncate text-zinc-800">{line.name}</div>
        <div className="text-[11px] text-zinc-500">
          {line.quantity} × default {fmt(line.default_unit_cost)}
        </div>
      </div>
      <div className="relative w-24">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">$</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={value}
          placeholder={String(line.default_unit_cost)}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full h-8 rounded border-2 pl-5 pr-1 text-sm text-right outline-none ${changed ? "border-amber-400 bg-amber-50" : "border-zinc-200"} focus:border-amber-500`}
          data-testid={`material-line-input-${line.index}`}
        />
      </div>
      <button
        onClick={() => onChange("")}
        disabled={value === ""}
        className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30"
        title="Use default"
        data-testid={`material-line-reset-${line.index}`}
      >
        <RotateCcw className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export const MaterialCostEditor = ({ invoiceId, onSaved, onLoaded }) => {
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const hydrate = (d) => {
    setData(d);
    onLoaded?.(d);
    setValues(Object.fromEntries(d.lines.map((l) => [l.index, l.actual_unit_cost ?? ""])));
  };

  useEffect(() => {
    axios
      .get(`${API}/invoices/${invoiceId}/material-costs`, { headers: { Authorization: `Bearer ${getSessionToken()}` } })
      .then((r) => hydrate(r.data))
      .catch(() => setData(null));
  }, [invoiceId]);

  if (!data || data.is_contractor || data.lines.length === 0) return null;

  const liveTotal = data.lines.reduce(
    (s, l) => s + (values[l.index] === "" ? l.default_unit_cost : Number(values[l.index]) || 0) * l.quantity, 0
  );

  const save = async () => {
    setSaving(true);
    try {
      const overrides = Object.fromEntries(
        data.lines.map((l) => [String(l.index), values[l.index] === "" ? null : Number(values[l.index]) || 0])
      );
      const r = await axios.patch(`${API}/invoices/${invoiceId}/material-costs`, { overrides }, {
        headers: { Authorization: `Bearer ${getSessionToken()}` },
      });
      hydrate(r.data);
      onSaved?.();
      toast.success("Material costs reconciled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save material costs");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-green-200 space-y-2" data-testid="material-cost-section">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5">
          <Package className="w-4 h-4 text-amber-600" /> Material Cost
          <span
            className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${data.reconciled_at ? "bg-green-100 text-green-800 border-green-200" : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}
            data-testid="material-reconciled-badge"
          >
            {data.reconciled_at ? "Reconciled" : "Estimated"}
          </span>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-zinc-900" data-testid="material-cost-total">{fmt(liveTotal)}</div>
          {Math.abs(liveTotal - data.default_total) > 0.004 && (
            <div className="text-[11px] text-zinc-500">default {fmt(data.default_total)}</div>
          )}
        </div>
      </div>
      <div className="text-[11px] text-zinc-500">Actual cost per unit. Leave blank to use the default.</div>
      {data.lines.map((l) => (
        <MaterialLine key={l.index} line={l} value={values[l.index]} onChange={(v) => setValues({ ...values, [l.index]: v })} />
      ))}
      <button
        onClick={save}
        disabled={saving}
        className="w-full h-9 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50"
        data-testid="save-material-cost-button"
      >
        {saving ? "Saving..." : data.reconciled_at ? "Update Material Costs" : "Save & Mark Reconciled"}
      </button>
    </div>
  );
};
